import React, { useState, useMemo, useCallback } from 'react';
import { ChatSession, GlobalConfig, AdBanner, User, ExamMode } from '../types';
import {
    MessageSquare, Plus, Trash2, X, Clock, MessageCircle, Crown,
    PenLine, Pin, Calendar, Tag, ChevronRight, Search, MoreHorizontal,
    Sparkles, Zap, Check, Coins, Diamond, Settings, Smartphone, Edit3,
    BookOpen, FolderOpen, ChevronDown, User as UserIcon, Ghost, FileText,
    Scale, GraduationCap, Users, Facebook, Map as MapIcon
} from 'lucide-react';
import AdDisplay from './AdDisplay';
import ActivePromoEffects from './ActivePromoEffects';
import { getPromoCodes, savePromoCodes } from '../services/firebaseService';
import { redeemPromoCode } from '../services/promoCodeService';
import { determineUserTier, getUserRank } from '../utils/rankUtils';
import { isNativeApp } from '../hooks/usePlatformDetection';
import APKSettings from './APKSettings';
import { ExamModeSelector, useExamMode } from './ExamModeSelector';
import { CodalReferenceModal } from './CodalReferenceModal';
import { useConfig } from '../contexts/ConfigContext';

// Hook to check if running on native platform with delayed detection
const useIsNativeApp = () => {
    const [isNative, setIsNative] = useState(false);

    React.useEffect(() => {
        // Check immediately
        setIsNative(isNativeApp());

        // Also check after a delay (for Capacitor initialization)
        const timer = setTimeout(() => {
            setIsNative(isNativeApp());
        }, 500);

        // Check when Capacitor might be ready
        const checkCapacitor = () => {
            if (typeof (window as any).Capacitor !== 'undefined') {
                setIsNative((window as any).Capacitor.isNativePlatform());
            }
        };

        // Multiple checks to ensure we catch Capacitor init
        const timers = [
            setTimeout(checkCapacitor, 100),
            setTimeout(checkCapacitor, 300),
            setTimeout(checkCapacitor, 1000),
        ];

        return () => {
            clearTimeout(timer);
            timers.forEach(t => clearTimeout(t));
        };
    }, []);

    return isNative;
};

// APK Settings Button Component - Only shown in native app
const APKSettingsButton: React.FC<{
    user?: any;
    onLogout?: () => void;
    showToast?: (type: 'success' | 'error' | 'warning' | 'info', message: string) => void;
}> = ({ user, onLogout, showToast }) => {
    const [showSettings, setShowSettings] = useState(false);

    return (
        <>
            <button
                onClick={() => setShowSettings(true)}
                className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl bg-gradient-to-r from-amber-500/10 to-orange-500/10 hover:from-amber-500/20 hover:to-orange-500/20 border border-amber-500/30 transition-all duration-200 group"
            >
                <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center shadow-lg shadow-amber-500/20">
                    <Settings size={16} className="text-white group-hover:rotate-90 transition-transform duration-300" />
                </div>
                <div className="flex-1 text-left">
                    <p className="text-xs font-bold text-white">App Settings</p>
                    <p className="text-[10px] text-amber-400/80">Biometrics, Performance, Security</p>
                </div>
                <div className="flex items-center gap-1 px-2 py-1 rounded-full bg-amber-500/20 border border-amber-500/30">
                    <Smartphone size={10} className="text-amber-400" />
                    <span className="text-[9px] font-bold text-amber-400">APK</span>
                </div>
            </button>

            <APKSettings
                isOpen={showSettings}
                onClose={() => setShowSettings(false)}
                isDarkMode={true}
                user={user}
                onLogout={onLogout}
                showToast={showToast}
            />
        </>
    );
};

// Sidebar Footer Component - Uses hook for native app detection
const SidebarFooter: React.FC<{
    user: any;
    isPremium: boolean;
    config: GlobalConfig;
    onOpenExtendModal: () => void;
    onOpenTokenPurchaseModal?: () => void;
    onOpenUpgradeModal: () => void;
    onUserUpdate: (user: any) => void;
    onLogout?: () => void;
    showToast?: (type: 'success' | 'error' | 'warning' | 'info', message: string) => void;
    onOpenSocial: () => void;
}> = ({ user, isPremium, config, onOpenExtendModal, onOpenTokenPurchaseModal, onOpenUpgradeModal, onUserUpdate, onLogout, showToast, onOpenSocial }) => {
    const isNative = useIsNativeApp();

    return (
        <div className="p-3 border-t border-slate-800 space-y-2">
            {/* Inject animated gold button styles */}
            <style dangerouslySetInnerHTML={{ __html: goldButtonStyles }} />

            {/* APK Settings Button - Only show in native app */}
            {isNative && (
                <APKSettingsButton user={user} onLogout={onLogout} showToast={showToast} />
            )}

            {/* Active Promo Effects */}
            {user && <ActivePromoEffects user={user} />}

            {isPremium ? (
                <PremiumCard
                    onOpenExtendModal={onOpenExtendModal}
                    onOpenTokenPurchaseModal={onOpenTokenPurchaseModal || onOpenUpgradeModal}
                    config={config}
                    user={user}
                    onUserUpdate={onUserUpdate}
                    onOpenSocial={onOpenSocial}
                />
            ) : user ? (
                /* Animated Dark Gold "GET TOKENS" Button - Only show for logged in users */
                <button
                    onClick={onOpenUpgradeModal}
                    className="relative w-full overflow-hidden rounded-xl group active:scale-[0.98] transition-transform"
                    style={{
                        background: 'linear-gradient(135deg, #8B6914 0%, #A67C00 25%, #8B6914 50%, #B8860B 75%, #8B6914 100%)',
                        backgroundSize: '400% 100%',
                        animation: 'goldShine 3s linear infinite, goldGlow 2s ease-in-out infinite',
                        boxShadow: '0 0 15px rgba(139, 105, 20, 0.4), 0 0 30px rgba(166, 124, 0, 0.25), inset 0 0 15px rgba(184, 134, 11, 0.15)',
                        minHeight: '48px',
                        touchAction: 'manipulation'
                    }}
                >
                    {/* Sparkle effects */}
                    <div className="absolute inset-0 pointer-events-none overflow-hidden">
                        {[...Array(6)].map((_, i) => (
                            <Sparkles
                                key={i}
                                size={12}
                                className="absolute text-amber-300/70"
                                style={{
                                    left: `${15 + i * 15}%`,
                                    top: `${20 + (i % 3) * 30}%`,
                                    animation: `sparkle 2s ease-in-out infinite ${i * 0.3}s`
                                }}
                            />
                        ))}

                        {/* Hover shimmer effect */}
                        <div
                            className="absolute inset-0 bg-gradient-to-r from-transparent via-amber-200/30 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700"
                            style={{ transform: 'skewX(-20deg)' }}
                        />
                    </div>

                    <div className="relative px-3 py-3.5 flex items-center justify-center gap-2">
                        <div className="absolute inset-0 bg-gradient-to-r from-black/10 via-transparent to-black/10" />
                        <div className="relative flex items-center gap-2">
                            <Zap size={16} className="text-amber-200 flex-shrink-0" />
                            <span className="text-amber-100 font-bold text-[13px] tracking-wide">GET TOKENS</span>
                            <Zap size={16} className="text-amber-200 flex-shrink-0" />
                        </div>
                    </div>
                </button>
            ) : (
                /* Guest - show sign in prompt */
                <div className="p-3 rounded-xl bg-slate-800/50 border border-slate-700/50 text-center">
                    <p className="text-xs text-slate-400 mb-2">Sign in to unlock upgrades</p>
                    <div className="flex items-center gap-2 px-2 py-1.5 text-slate-500 text-[10px] justify-center">
                        <Clock size={12} />
                        <span>History saved locally</span>
                    </div>
                </div>
            )}

            {/* Facebook Social Link */}
            <a
                href="http://facebook.com/lucareview"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 w-full py-2 rounded-xl bg-blue-900/20 hover:bg-blue-900/40 border border-blue-900/30 text-blue-400 text-xs font-medium transition-all group"
            >
                <Facebook size={16} className="group-hover:scale-110 transition-transform" />
                <span>More tokens? Follow us on Facebook!</span>
            </a>
        </div>
    );
};

// Animated dark gold upgrade button styles
const goldButtonStyles = `
@keyframes goldShine {
    0% { background-position: -200% center; }
    100% { background-position: 200% center; }
}
@keyframes goldGlow {
    0%, 100% { box-shadow: 0 0 15px rgba(139, 105, 20, 0.4), 0 0 30px rgba(166, 124, 0, 0.25), inset 0 0 15px rgba(184, 134, 11, 0.15); }
    50% { box-shadow: 0 0 25px rgba(139, 105, 20, 0.55), 0 0 45px rgba(166, 124, 0, 0.35), inset 0 0 25px rgba(184, 134, 11, 0.2); }
}
@keyframes sparkle {
    0%, 100% { opacity: 0; transform: scale(0) rotate(0deg); }
    50% { opacity: 1; transform: scale(1) rotate(180deg); }
}
`;

// Premium Card Component - Now opens TokenPurchaseModal for more tokens
const PremiumCard: React.FC<{
    onOpenExtendModal: () => void;
    onOpenTokenPurchaseModal: () => void;
    config: GlobalConfig;
    user: any;
    onUserUpdate: (user: any) => void;
    onOpenSocial: () => void;
}> = ({ onOpenExtendModal, onOpenTokenPurchaseModal, config, user, onUserUpdate, onOpenSocial }) => {
    const [showPromo, setShowPromo] = useState(false);
    const [promoCode, setPromoCode] = useState('');
    const [promoError, setPromoError] = useState('');
    const [isApplying, setIsApplying] = useState(false);
    const [expiryDisplay, setExpiryDisplay] = React.useState('');

    // Use centralized tier detection
    const userTier = user ? determineUserTier(user) : 'standard';
    const isMaxTier = userTier === 'max';
    const isPremium = userTier === 'premium';
    const rankConfig = user && config ? getUserRank(user, config) : null;
    const tierName = rankConfig?.name || (isMaxTier ? 'MAX' : isPremium ? 'Premium' : 'Standard');
    const tierColor = isMaxTier ? 'from-purple-500 via-pink-500 to-rose-500' : isPremium ? 'bg-amber-500/20' : 'bg-slate-500/20';
    const tierTextColor = isMaxTier ? 'text-purple-400' : isPremium ? 'text-amber-400' : 'text-slate-400';

    // Use correct expiry date based on tier
    const actualExpiryDate = isMaxTier
        ? (user?.premiumExpiryDate) // MAX users use premiumExpiryDate
        : isPremium
            ? user?.premiumExpiryDate
            : null;

    React.useEffect(() => {
        const updateExpiryTime = () => {
            if (!actualExpiryDate) {
                setExpiryDisplay('');
                return;
            }
            const currentTime = Date.now();
            const diff = new Date(actualExpiryDate).getTime() - currentTime;
            if (diff <= 0) {
                setExpiryDisplay('Expired');
            } else {
                const days = Math.floor(diff / (1000 * 60 * 60 * 24));
                const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
                setExpiryDisplay(days > 0 ? `${days}d ${hours}h left` : `${hours}h left`);
            }
        };

        updateExpiryTime(); // Initial calculation
        const timer = setInterval(updateExpiryTime, 1000); // Update every second
        return () => clearInterval(timer);
    }, [actualExpiryDate]);

    // Calculate time remaining for display
    const currentTime = Date.now();
    const timeRemaining = actualExpiryDate ? new Date(actualExpiryDate).getTime() - currentTime : 0;

    const handleApplyPromo = async () => {
        if (!promoCode.trim()) return;
        setIsApplying(true);
        setPromoError('');
        try {
            const promoCodes = await getPromoCodes();
            const code = promoCodes.find(c => c.code.toUpperCase() === promoCode.trim().toUpperCase());
            if (!code) { setPromoError('Invalid code'); setIsApplying(false); return; }
            if (!code.isActive) { setPromoError('Code inactive'); setIsApplying(false); return; }
            if (code.validUntil && new Date() > new Date(code.validUntil)) { setPromoError('Expired'); setIsApplying(false); return; }
            if (code.maxUses && code.usedCount !== undefined && code.usedCount >= code.maxUses) { setPromoError('Limit reached'); setIsApplying(false); return; }
            const result = await redeemPromoCode(code, user, promoCodes, savePromoCodes);
            if (result.success && result.updatedUser) {
                onUserUpdate(result.updatedUser);
                setPromoCode('');
                setShowPromo(false);
            } else {
                setPromoError(result.error || 'Failed');
            }
        } catch (error) {
            setPromoError('Error');
        }
        setIsApplying(false);
    };

    return (
        <div className="rounded-xl p-3 bg-slate-800/50 border border-slate-700/50">
            <div className="flex items-center gap-2 mb-2">
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${isMaxTier ? 'bg-gradient-to-br ' + tierColor : tierColor}`}>
                    {isMaxTier ? <Diamond size={16} className="text-white" /> : <Crown size={16} className="text-amber-400" />}
                </div>
                <div className="flex-1 min-w-0">
                    <p className={`text-xs font-semibold ${isMaxTier ? 'bg-gradient-to-r from-purple-400 via-pink-400 to-rose-400 bg-clip-text text-transparent' : isPremium ? 'text-white' : 'text-slate-300'}`}>
                        {tierName} {(isMaxTier || isPremium) ? 'Active' : 'Account'}
                    </p>
                    {actualExpiryDate && (
                        <p className={`text-[10px] font-mono ${timeRemaining > 0 ? 'text-slate-400' : 'text-red-400'}`}>
                            {timeRemaining > 0 ? expiryDisplay : '⚠️ Expired'}
                        </p>
                    )}
                </div>
            </div>
            {!showPromo ? (
                <div className="flex gap-2">
                    {/* Get More Tokens button - opens TokenPurchaseModal */}
                    <button
                        onClick={onOpenTokenPurchaseModal}
                        className={`flex-1 py-2 text-[11px] font-medium rounded-lg flex items-center justify-center gap-1.5 transition-colors ${isMaxTier
                            ? 'bg-purple-500/20 text-purple-400 hover:bg-purple-500/30'
                            : 'bg-amber-500/20 text-amber-400 hover:bg-amber-500/30'
                            }`}
                    >
                        <Coins size={12} />
                        Get Tokens
                    </button>
                    <button onClick={onOpenSocial} className="flex-1 py-2 text-[11px] font-medium rounded-lg bg-slate-700/50 text-slate-300 hover:bg-slate-700 transition-colors flex items-center justify-center gap-1.5">
                        <Users size={12} />
                        Social
                    </button>
                </div>
            ) : (
                <div className="space-y-2">
                    <div className="flex gap-1.5">
                        <input
                            type="text"
                            value={promoCode}
                            onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                            placeholder="CODE"
                            className="flex-1 bg-slate-900/50 border border-slate-600 rounded-lg py-1.5 px-2 text-[11px] text-white focus:outline-none focus:border-amber-500 font-mono"
                        />
                        <button onClick={handleApplyPromo} disabled={isApplying || !promoCode.trim()} className="px-3 py-1.5 bg-amber-500 text-slate-900 text-[11px] font-semibold rounded-lg disabled:opacity-50">
                            {isApplying ? '...' : 'Go'}
                        </button>
                    </div>
                    {promoError && <p className="text-[10px] text-red-400">{promoError}</p>}
                    <button onClick={() => { setShowPromo(false); setPromoError(''); setPromoCode(''); }} className="w-full text-[10px] text-slate-500 hover:text-slate-400">
                        Cancel
                    </button>
                </div>
            )}
        </div>
    );
};

// Session Item Component
interface SessionItemProps {
    session: ChatSession;
    isActive: boolean;
    onSelect: () => void;
    onPin: () => void;
    onRename: (newTitle: string) => void;
    onDelete: () => void;
}

const SessionItem: React.FC<SessionItemProps> = React.memo(({ session, isActive, onSelect, onPin, onRename, onDelete }) => {
    const [showMenu, setShowMenu] = useState(false);
    const [isRenaming, setIsRenaming] = useState(false);
    const [renameValue, setRenameValue] = useState(session.title);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const renameInputRef = React.useRef<HTMLInputElement>(null);

    // Focus input when entering rename mode
    React.useEffect(() => {
        if (isRenaming) {
            setTimeout(() => renameInputRef.current?.select(), 50);
        }
    }, [isRenaming]);

    const handleRenameSubmit = () => {
        const trimmed = renameValue.trim();
        if (trimmed && trimmed !== session.title) {
            onRename(trimmed);
        }
        setIsRenaming(false);
    };

    const handleRenameKeyDown = (e: React.KeyboardEvent) => {
        e.stopPropagation();
        if (e.key === 'Enter') handleRenameSubmit();
        if (e.key === 'Escape') { setIsRenaming(false); setRenameValue(session.title); }
    };

    // Derive useful info
    const msgCount = session.messages.length;
    const lastMsg = session.messages[msgCount - 1];
    const lastMsgPreview = lastMsg
        ? (lastMsg.role === 'user' ? 'You: ' : '') + lastMsg.content.replace(/[#*`\n]/g, ' ').trim().slice(0, 50)
        : '';
    const timeAgo = (() => {
        const ts = session.lastModified || session.updatedAt || session.createdAt;
        if (!ts) return '';
        const diff = Date.now() - (typeof ts === 'number' ? ts : new Date(ts).getTime());
        const mins = Math.floor(diff / 60000);
        if (mins < 1) return 'now';
        if (mins < 60) return `${mins}m`;
        const hrs = Math.floor(mins / 60);
        if (hrs < 24) return `${hrs}h`;
        const days = Math.floor(hrs / 24);
        return `${days}d`;
    })();

    return (
        <div
            className={`group relative flex flex-col gap-0.5 px-2.5 py-2 rounded-xl cursor-pointer transition-all duration-200 ${isActive
                ? 'bg-slate-800 text-white shadow-sm ring-1 ring-slate-700'
                : 'text-slate-400 hover:bg-slate-800/50 hover:text-slate-200'
                }`}
            onClick={isRenaming ? undefined : onSelect}
        >
            {/* Top row: icon + title + menu */}
            <div className="flex items-center gap-2 min-w-0">
                {session.pinned && (
                    <Pin size={9} className="flex-shrink-0 text-amber-500 fill-current" />
                )}
                <MessageSquare size={13} className={`flex-shrink-0 ${isActive ? 'text-blue-400' : 'text-slate-600'}`} />

                {isRenaming ? (
                    <input
                        ref={renameInputRef}
                        value={renameValue}
                        onChange={(e) => setRenameValue(e.target.value)}
                        onKeyDown={handleRenameKeyDown}
                        onBlur={handleRenameSubmit}
                        onClick={(e) => e.stopPropagation()}
                        className="flex-1 min-w-0 bg-slate-700 text-white text-xs font-medium px-1.5 py-0.5 rounded outline-none ring-1 ring-blue-500"
                        maxLength={60}
                    />
                ) : (
                    <span className="flex-1 truncate text-xs font-medium">{session.title}</span>
                )}

                {/* Time badge */}
                {!isRenaming && timeAgo && (
                    <span className="flex-shrink-0 text-[9px] text-slate-600 tabular-nums">{timeAgo}</span>
                )}

                {/* Action Menu Button */}
                {!isRenaming && (
                    <button
                        onClick={(e) => { e.stopPropagation(); setShowMenu(!showMenu); setShowDeleteConfirm(false); }}
                        className={`p-1 rounded flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity ${showMenu ? 'opacity-100 bg-slate-700' : 'hover:bg-slate-700'}`}
                    >
                        <MoreHorizontal size={12} className="text-slate-400" />
                    </button>
                )}
            </div>

            {/* Bottom row: preview + message count */}
            {!isRenaming && lastMsgPreview && (
                <div className="flex items-center gap-1.5 pl-[21px]">
                    <span className="flex-1 truncate text-[10px] text-slate-600 leading-tight">{lastMsgPreview}</span>
                    {msgCount > 0 && (
                        <span className="flex-shrink-0 text-[9px] text-slate-600 tabular-nums">{msgCount} msg{msgCount !== 1 ? 's' : ''}</span>
                    )}
                </div>
            )}

            {/* Dropdown Menu */}
            {showMenu && (
                <>
                    <div className="fixed inset-0 z-40" onClick={(e) => { e.stopPropagation(); setShowMenu(false); setShowDeleteConfirm(false); }} />
                    <div className="absolute right-0 top-full mt-1 z-50 bg-slate-800 border border-slate-700 rounded-xl shadow-xl py-1 min-w-[130px] animate-in fade-in zoom-in-95 duration-150">
                        <button onClick={(e) => { e.stopPropagation(); onPin(); setShowMenu(false); }} className="w-full flex items-center gap-2 px-3 py-1.5 text-[11px] text-slate-300 hover:bg-slate-700 transition-colors rounded-t-lg">
                            <Pin size={11} className={session.pinned ? 'fill-current text-amber-400' : ''} />
                            {session.pinned ? 'Unpin' : 'Pin'}
                        </button>
                        <button onClick={(e) => { e.stopPropagation(); setIsRenaming(true); setRenameValue(session.title); setShowMenu(false); }} className="w-full flex items-center gap-2 px-3 py-1.5 text-[11px] text-slate-300 hover:bg-slate-700 transition-colors">
                            <PenLine size={11} /> Rename
                        </button>
                        <div className="h-px bg-slate-700 my-1" />
                        {showDeleteConfirm ? (
                            <div className="px-3 py-2 space-y-1.5">
                                <p className="text-[10px] text-red-400 font-medium">Delete this chat?</p>
                                <div className="flex gap-1.5">
                                    <button onClick={(e) => { e.stopPropagation(); onDelete(); setShowMenu(false); setShowDeleteConfirm(false); }} className="flex-1 px-2 py-1 text-[10px] font-medium bg-red-600 text-white rounded-md hover:bg-red-500 transition-colors">
                                        Delete
                                    </button>
                                    <button onClick={(e) => { e.stopPropagation(); setShowDeleteConfirm(false); }} className="flex-1 px-2 py-1 text-[10px] font-medium bg-slate-700 text-slate-300 rounded-md hover:bg-slate-600 transition-colors">
                                        Cancel
                                    </button>
                                </div>
                            </div>
                        ) : (
                            <button onClick={(e) => { e.stopPropagation(); setShowDeleteConfirm(true); }} className="w-full flex items-center gap-2 px-3 py-1.5 text-[11px] text-red-400 hover:bg-red-900/20 transition-colors rounded-b-lg">
                                <Trash2 size={11} /> Delete
                            </button>
                        )}
                    </div>
                </>
            )}
        </div>
    );
});

SessionItem.displayName = 'SessionItem';

interface SidebarProps {
    sessions: ChatSession[];
    currentSessionId: string | null;
    onSelectSession: (id: string) => void;
    onNewChat: () => void;
    onDeleteSession: (id: string) => void;
    onRenameSession: (sessionId: string, newTitle: string) => void;
    onPinSession: (id: string, isPinned: boolean) => void;
    isOpen: boolean;
    isMobileOpen: boolean;
    onCloseMobile: () => void;
    isPremium: boolean;
    config: GlobalConfig;
    onOpenUpgradeModal: () => void;
    onOpenExtendModal: () => void;
    onOpenTokenPurchaseModal?: () => void; // For "Get More Tokens" functionality
    ads: AdBanner[];
    user: any;
    onUserUpdate: (user: any) => void;
    onOpenSessionFiles?: () => void; // For opening session files modal
    onLogout?: () => void; // For logging out from APK settings
    showToast?: (type: 'success' | 'error' | 'warning' | 'info', message: string) => void;
    onOpenSocial: () => void;
    onOpenMasteryPath?: () => void; // For opening the Mastery Path modal
}

const Sidebar: React.FC<SidebarProps> = ({
    sessions,
    currentSessionId,
    onSelectSession,
    onNewChat,
    onDeleteSession,
    onRenameSession,
    onPinSession,
    isOpen,
    isMobileOpen,
    onCloseMobile,
    isPremium,
    config,
    onOpenUpgradeModal,
    onOpenExtendModal,
    onOpenTokenPurchaseModal,
    ads,
    user,
    onUserUpdate,
    onOpenSessionFiles,
    onLogout,
    showToast,
    onOpenSocial,
    onOpenMasteryPath
}) => {
    const [searchQuery, setSearchQuery] = useState('');
    const [showSessions, setShowSessions] = useState(true);
    const [showCodalReference, setShowCodalReference] = useState(false);

    const { contentConfig, featureConfig } = useConfig();

    // Exam mode state
    const { examMode, changeExamMode, isBarMode } = useExamMode(user, onUserUpdate);

    // Files view removed - now opens modal via onOpenSessionFiles
    const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>({
        'Pinned': true, 'Today': true, 'Yesterday': true, 'Previous 7 Days': true, 'Older': true
    });

    // Filter and group sessions
    const { pinnedSessions, groupedSessions, groupOrder } = useMemo(() => {
        const filtered = searchQuery
            ? sessions.filter(s => s.title.toLowerCase().includes(searchQuery.toLowerCase()))
            : sessions;

        const pinned = filtered.filter(s => s.pinned);
        const unpinned = filtered.filter(s => !s.pinned);

        const groups = unpinned.reduce((acc, session) => {
            const date = new Date(session.lastModified || session.updatedAt);
            const today = new Date();
            const yesterday = new Date(today);
            yesterday.setDate(yesterday.getDate() - 1);

            let key = 'Older';
            if (date.toDateString() === today.toDateString()) key = 'Today';
            else if (date.toDateString() === yesterday.toDateString()) key = 'Yesterday';
            else if (today.getTime() - date.getTime() < 7 * 24 * 60 * 60 * 1000) key = 'Previous 7 Days';

            if (!acc[key]) acc[key] = [];
            acc[key].push(session);
            return acc;
        }, {} as Record<string, ChatSession[]>);

        return {
            pinnedSessions: pinned,
            groupedSessions: groups,
            groupOrder: ['Today', 'Yesterday', 'Previous 7 Days', 'Older']
        };
    }, [sessions, searchQuery]);

    const toggleGroup = useCallback((group: string) => {
        setExpandedGroups(prev => ({ ...prev, [group]: !prev[group] }));
    }, []);

    const handleSelectSession = useCallback((id: string) => {
        onSelectSession(id);
        onCloseMobile();
    }, [onSelectSession, onCloseMobile]);

    const salePrice = config.isSaleActive
        ? config.proPrice * (1 - config.saleDiscountPercent / 100)
        : config.proPrice;

    return (
        <>
            {/* Mobile Backdrop */}
            <div
                className={`fixed inset-0 bg-black/60 backdrop-blur-sm z-40 md:hidden transition-opacity duration-300 ${isMobileOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
                    }`}
                onClick={onCloseMobile}
            />

            {/* Sidebar */}
            <aside
                className={`fixed top-0 left-0 z-40 bg-slate-900 flex flex-col border-r border-slate-800 transition-all duration-300 ease-in-out ${isMobileOpen ? 'translate-x-0 w-72' : '-translate-x-full w-72'
                    } md:relative md:translate-x-0 md:flex-shrink-0 ${isOpen ? 'md:w-72' : 'md:w-0 md:border-0'}`}
                style={{
                    height: '100dvh',
                    paddingTop: 'env(safe-area-inset-top, 0px)',
                    paddingBottom: 'env(safe-area-inset-bottom, 0px)',
                    paddingLeft: 'env(safe-area-inset-left, 0px)',
                    overflow: 'hidden',
                    whiteSpace: 'nowrap' // Prevent text wrapping when closing
                }}
            >
                <div
                    className={`flex flex-col h-full w-72 transition-all duration-200 ${isOpen || isMobileOpen ? 'opacity-100' : 'opacity-0'}`}
                    style={{
                        height: '100%',
                        overflow: 'hidden',
                        minWidth: '18rem'
                    }}
                >

                    {/* Header */}
                    <div className="p-3 border-b border-slate-800/80">
                        {/* Mobile Header */}
                        <div className="flex items-center justify-between mb-3 md:hidden">
                            <div className="flex items-center gap-2.5">
                                <div className="w-9 h-9 rounded-xl overflow-hidden bg-slate-800 ring-1 ring-slate-700/50 shadow-sm">
                                    <img src="/robot-luca.png" alt="Luca" className="w-full h-full object-cover" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                                </div>
                                <div>
                                    <h2 className="text-sm font-bold text-white tracking-tight">{contentConfig?.appName || 'Luca'}</h2>
                                    <p className="text-[10px] text-slate-500 font-medium">{contentConfig?.tagline || 'AI Reviewer'}</p>
                                </div>
                            </div>
                            <button onClick={onCloseMobile} className="p-2 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800 transition-all active:scale-95">
                                <X size={18} />
                            </button>
                        </div>

                        {/* New Chat Button - Enhanced with shine */}
                        <button
                            onClick={() => { onNewChat(); onCloseMobile(); }}
                            data-tour="new-chat"
                            className="group relative w-full flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-500 hover:to-blue-600 text-white rounded-xl font-semibold text-sm transition-all duration-200 active:scale-[0.97] shadow-lg shadow-blue-600/25 hover:shadow-blue-500/35 overflow-hidden"
                        >
                            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700" />
                            <Plus size={18} strokeWidth={2.5} className="relative z-10" />
                            <span className="relative z-10">New Chat</span>
                        </button>

                        {/* Search + Exam Mode - Combined Row */}
                        <div className="flex gap-2 mt-2.5">
                            <div className="relative flex-1">
                                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
                                <input
                                    type="text"
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    placeholder="Search chats..."
                                    className="w-full pl-9 pr-3 py-2 bg-slate-800/60 border border-slate-700/40 rounded-lg text-xs text-white placeholder:text-slate-500 focus:outline-none focus:border-blue-500/50 focus:ring-1 focus:ring-blue-500/20 transition-all"
                                />
                            </div>
                            <ExamModeSelector
                                user={user}
                                currentMode={examMode}
                                onModeChange={changeExamMode}
                                compact
                            />
                        </div>

                        {/* Quick Actions - Compact icon row */}
                        <div className="flex gap-1.5 mt-2">
                            <button
                                onClick={onOpenMasteryPath}
                                className="group relative flex-1 flex items-center justify-center gap-1.5 px-2 py-2 rounded-lg text-[11px] font-semibold transition-all overflow-hidden bg-gradient-to-r from-amber-500/20 via-orange-500/20 to-amber-500/20 border border-amber-500/40 text-amber-300 hover:border-amber-400 hover:text-amber-200 hover:shadow-lg hover:shadow-amber-500/20 active:scale-[0.97]"
                                title="Mastery Path"
                            >
                                {/* Shimmer overlay */}
                                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-amber-400/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000 ease-in-out" />
                                {/* Glow pulse */}
                                <div className="absolute inset-0 rounded-lg bg-amber-500/10 animate-pulse opacity-60" />
                                <MapIcon size={13} className="relative z-10" />
                                <span className="relative z-10">Mastery Path</span>
                                <Sparkles size={10} className="relative z-10 text-amber-400/70" />
                            </button>
                            {isBarMode && (
                                <button
                                    onClick={() => setShowCodalReference(true)}
                                    className="flex-1 flex items-center justify-center gap-1.5 px-2 py-2 rounded-lg text-[11px] font-medium transition-all bg-purple-500/10 border border-purple-500/20 text-purple-400 hover:bg-purple-500/20 hover:border-purple-400"
                                    title="Legal Code Reference"
                                >
                                    <Scale size={12} />
                                    <span>Codal</span>
                                </button>
                            )}
                        </div>
                    </div>

                    {/* Sessions List */}
                    <nav
                        className="flex-1 overflow-y-auto p-2 scrollbar-thin scrollbar-thumb-slate-700 scrollbar-track-transparent"
                        style={{
                            minHeight: 0, /* Flexbox scrolling fix */
                            WebkitOverflowScrolling: 'touch',
                            overscrollBehavior: 'contain'
                        }}
                    >
                        {sessions.length === 0 ? (
                            <div className="flex flex-col items-center justify-center h-40 text-slate-500">
                                <div className="w-12 h-12 rounded-2xl bg-slate-800/50 flex items-center justify-center mb-3 empty-state-icon">
                                    <MessageCircle size={22} className="text-slate-500" />
                                </div>
                                <span className="text-xs font-medium">No conversations yet</span>
                                <span className="text-[10px] text-slate-600 mt-0.5">Start a new chat to begin</span>
                            </div>
                        ) : (
                            <>
                                {/* Pinned Sessions */}
                                {pinnedSessions.length > 0 && (
                                    <div className="mb-2">
                                        <button
                                            onClick={() => toggleGroup('Pinned')}
                                            className="w-full flex items-center justify-between px-2 py-1.5 text-[10px] font-semibold text-slate-500 uppercase tracking-wider hover:text-slate-400 transition-colors"
                                        >
                                            <span className="flex items-center gap-1">
                                                <Pin size={10} className="text-amber-500" /> Pinned
                                            </span>
                                            <ChevronRight size={12} className={`transition-transform ${expandedGroups['Pinned'] ? 'rotate-90' : ''}`} />
                                        </button>
                                        {expandedGroups['Pinned'] && (
                                            <div className="space-y-0.5 animate-in slide-in-from-top-2 duration-200">
                                                {pinnedSessions.map(session => (
                                                    <SessionItem
                                                        key={session.id}
                                                        session={session}
                                                        isActive={currentSessionId === session.id}
                                                        onSelect={() => handleSelectSession(session.id)}
                                                        onPin={() => onPinSession(session.id, !session.pinned)}
                                                        onRename={(newTitle) => onRenameSession(session.id, newTitle)}
                                                        onDelete={() => onDeleteSession(session.id)}
                                                    />
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                )}

                                {/* Grouped Sessions */}
                                {groupOrder.map(group => {
                                    const groupSessions = groupedSessions[group];
                                    if (!groupSessions || groupSessions.length === 0) return null;

                                    return (
                                        <div key={group} className="mb-2">
                                            <button
                                                onClick={() => toggleGroup(group)}
                                                className="w-full flex items-center justify-between px-2 py-1.5 text-[10px] font-semibold text-slate-500 uppercase tracking-wider hover:text-slate-400 transition-colors"
                                            >
                                                <span>{group}</span>
                                                <ChevronRight size={12} className={`transition-transform ${expandedGroups[group] ? 'rotate-90' : ''}`} />
                                            </button>
                                            {expandedGroups[group] && (
                                                <div className="space-y-0.5 animate-in slide-in-from-top-2 duration-200">
                                                    {groupSessions.sort((a, b) => new Date(b.lastModified || 0).getTime() - new Date(a.lastModified || 0).getTime()).map(session => (
                                                        <SessionItem
                                                            key={session.id}
                                                            session={session}
                                                            isActive={currentSessionId === session.id}
                                                            onSelect={() => handleSelectSession(session.id)}
                                                            onPin={() => onPinSession(session.id, !session.pinned)}
                                                            onRename={(newTitle) => onRenameSession(session.id, newTitle)}
                                                            onDelete={() => onDeleteSession(session.id)}
                                                        />
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </>
                        )}

                        {/* Ads */}
                        <AdDisplay position="sidebar" ads={ads} />
                    </nav>

                    {/* Footer */}
                    <SidebarFooter
                        user={user}
                        isPremium={isPremium}
                        config={config}
                        onOpenExtendModal={onOpenExtendModal}
                        onOpenTokenPurchaseModal={onOpenTokenPurchaseModal}
                        onOpenUpgradeModal={onOpenUpgradeModal}
                        onUserUpdate={onUserUpdate}
                        onLogout={onLogout}
                        showToast={showToast}
                        onOpenSocial={onOpenSocial}
                    />
                </div>
            </aside>

            {/* Codal Reference Modal */}
            <CodalReferenceModal
                isOpen={showCodalReference}
                onClose={() => setShowCodalReference(false)}
            />
        </>
    );
};

export default Sidebar;
